import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarClock, ExternalLink, Layers, Plug, Slack, SquarePen, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
import { REDESIGN_ROW_FILL_INSET_CLASS } from "./compactSessionRowStyles";

/** Primary navigation pinned above the session lists. */

const NAV_ROW = "group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white";
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
const REDESIGN_NAV_ROW = cn("group flex h-8 w-full items-center gap-1.5 rounded-[4px] px-2 type-body-s-regular cursor-pointer transition-none text-neutral-700 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-border)] [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", REDESIGN_ROW_FILL_INSET_CLASS);
function navClasses(variant: CodesmithSidebarVariant) {
  return {
    nav: variant === "redesign" ? "flex flex-col px-2 pb-4" : "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)] pb-2",
    row: variant === "redesign" ? REDESIGN_NAV_ROW : NAV_ROW,
    icon: variant === "redesign" ? "size-4 shrink-0" : "size-[18px] shrink-0"
  };
}

// The shortcut keycaps stay hidden until the row is hovered or
// keyboard-focused, keeping the resting sidebar clean. Opacity (not display)
// preserves the row's layout so the label doesn't shift when they appear.
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(14);
  const {
    icon: Icon,
    label,
    shortcut,
    external: t1,
    iconClassName: t2
  } = t0;
  const external = t1 === undefined ? false : t1;
  const iconClassName = t2 === undefined ? "size-[18px] shrink-0" : t2;
  let t3;
  if ($[0] !== Icon || $[1] !== iconClassName) {
    t3 = <Icon className={iconClassName} />;
    $[0] = Icon;
    $[1] = iconClassName;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label) {
    t4 = <span className="flex-1 truncate text-left">{label}</span>;
    $[3] = label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== shortcut) {
    t5 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[5] = shortcut;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== external) {
    t6 = external ? <ExternalLink aria-hidden={true} className="size-3.5 shrink-0" /> : null;
    $[7] = external;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <>{t3}{t4}{t5}{t6}</>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /** Set once Slack is fully connected; the Slack row then opens the DM. */
  slackDmUrl?: string | null;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "connect-slack" | "automations" | "mcp-connections" | "machine-definitions" | null;
  className?: string;
  variant?: CodesmithSidebarVariant;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(53);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    mcpConnectionsHref,
    slackDmUrl,
    onNavigate,
    newSessionShortcut: t1,
    automationsShortcut: t2,
    activeRow: t3,
    className,
    variant: t4
  } = t0;
  const newSessionShortcut = t1 === undefined ? "\u2325\u21E7N" : t1;
  const automationsShortcut = t2 === undefined ? "\u2325\u21E7A" : t2;
  const activeRow = t3 === undefined ? null : t3;
  const variant = t4 === undefined ? "legacy" : t4;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  const machineDefinitionsHref = typeof org === "string" ? `/${org}/codesmith/machine-definitions` : "/codesmith/machine-definitions";
  const connectSlackHref = typeof org === "string" ? `/${org}/codesmith/connect-slack` : "/codesmith/connect-slack";
  let NAV;
  let classes;
  let t5;
  let t6;
  let t7;
  if ($[0] !== activeRow || $[1] !== className || $[2] !== connectSlackHref || $[3] !== newSessionHref || $[4] !== newSessionShortcut || $[5] !== onNavigate || $[6] !== onNewSession || $[7] !== slackDmUrl || $[8] !== variant) {
    classes = navClasses(variant);
    NAV = classes.row;
    const redesign = variant === "redesign";
    t5 = cn(classes.nav, className);
    let t8;
    if ($[14] !== onNewSession) {
      t8 = e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }
        e.preventDefault();
        onNewSession();
      };
      $[14] = onNewSession;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t6 = <Link href={newSessionHref} onClick={t8} aria-keyshortcuts="Alt+Shift+N" aria-current={activeRow === "new-session" ? "page" : undefined} className={cn(NAV, activeRow === "new-session" && NAV_ROW_ACTIVE)}><RowContents icon={SquarePen} label={redesign ? "New Chat" : "New session"} shortcut={newSessionShortcut} iconClassName={classes.icon} /></Link>;
    t7 = slackDmUrl ? <a href={slackDmUrl} target="_blank" rel="noopener noreferrer" className={NAV}><RowContents icon={Slack} label="Open in Slack" external={true} iconClassName={classes.icon} /></a> : <Link href={connectSlackHref} onClick={onNavigate} aria-current={activeRow === "connect-slack" ? "page" : undefined} className={cn(NAV, activeRow === "connect-slack" && NAV_ROW_ACTIVE)}><RowContents icon={Slack} label="Connect Slack" iconClassName={classes.icon} /></Link>;
    $[0] = activeRow;
    $[1] = className;
    $[2] = connectSlackHref;
    $[3] = newSessionHref;
    $[4] = newSessionShortcut;
    $[5] = onNavigate;
    $[6] = onNewSession;
    $[7] = slackDmUrl;
    $[8] = variant;
    $[9] = NAV;
    $[10] = classes;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    NAV = $[9];
    classes = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[16] !== NAV || $[17] !== activeRow || $[18] !== automationsHref || $[19] !== automationsShortcut || $[20] !== classes.icon || $[21] !== onNavigate || $[22] !== onPrefetchAutomations) {
    t8 = automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={CalendarClock} label="Automations" shortcut={automationsShortcut} iconClassName={classes.icon} /></Link> : null;
    $[16] = NAV;
    $[17] = activeRow;
    $[18] = automationsHref;
    $[19] = automationsShortcut;
    $[20] = classes.icon;
    $[21] = onNavigate;
    $[22] = onPrefetchAutomations;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const t9 = activeRow === "mcp-connections" ? "page" : undefined;
  const t10 = activeRow === "mcp-connections" && NAV_ROW_ACTIVE;
  let t11;
  if ($[24] !== NAV || $[25] !== t10) {
    t11 = cn(NAV, t10);
    $[24] = NAV;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== classes.icon) {
    t12 = <RowContents icon={Plug} label="MCP Connections" iconClassName={classes.icon} />;
    $[27] = classes.icon;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== mcpConnectionsHref || $[30] !== onNavigate || $[31] !== t11 || $[32] !== t12 || $[33] !== t9) {
    t13 = <Link href={mcpConnectionsHref} onClick={onNavigate} aria-current={t9} className={t11}>{t12}</Link>;
    $[29] = mcpConnectionsHref;
    $[30] = onNavigate;
    $[31] = t11;
    $[32] = t12;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = activeRow === "machine-definitions" ? "page" : undefined;
  const t15 = activeRow === "machine-definitions" && NAV_ROW_ACTIVE;
  let t16;
  if ($[35] !== NAV || $[36] !== t15) {
    t16 = cn(NAV, t15);
    $[35] = NAV;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== classes.icon) {
    t17 = <RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} />;
    $[38] = classes.icon;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== machineDefinitionsHref || $[41] !== onNavigate || $[42] !== t14 || $[43] !== t16 || $[44] !== t17) {
    t18 = <Link href={machineDefinitionsHref} onClick={onNavigate} aria-current={t14} className={t16}>{t17}</Link>;
    $[40] = machineDefinitionsHref;
    $[41] = onNavigate;
    $[42] = t14;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t13 || $[47] !== t18 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8) {
    t19 = <nav className={t5}>{t6}{t7}{t8}{t13}{t18}</nav>;
    $[46] = t13;
    $[47] = t18;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(40);
  const {
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "legacy" : t1;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  let t2;
  if ($[0] !== isHydrated || $[1] !== org) {
    t2 = isHydrated && readCachedAutomationsEnabled(typeof org === "string" ? org : null);
    $[0] = isHydrated;
    $[1] = org;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const showAutomations = t2;
  let classes;
  let redesign;
  let t3;
  if ($[3] !== className || $[4] !== variant) {
    classes = navClasses(variant);
    redesign = variant === "redesign";
    t3 = cn(classes.nav, className);
    $[3] = className;
    $[4] = variant;
    $[5] = classes;
    $[6] = redesign;
    $[7] = t3;
  } else {
    classes = $[5];
    redesign = $[6];
    t3 = $[7];
  }
  const t4 = redesign ? "New Chat" : "New session";
  let t5;
  if ($[8] !== classes.icon || $[9] !== t4) {
    t5 = <RowContents icon={SquarePen} label={t4} iconClassName={classes.icon} />;
    $[8] = classes.icon;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== classes.row || $[12] !== t5) {
    t6 = <div className={classes.row}>{t5}</div>;
    $[11] = classes.row;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== classes.icon) {
    t7 = <RowContents icon={Slack} label="Connect Slack" iconClassName={classes.icon} />;
    $[14] = classes.icon;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== classes.row || $[17] !== t7) {
    t8 = <div className={classes.row}>{t7}</div>;
    $[16] = classes.row;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== classes.icon || $[20] !== classes.row || $[21] !== showAutomations) {
    t9 = showAutomations && <div className={classes.row}><RowContents icon={CalendarClock} label="Automations" iconClassName={classes.icon} /></div>;
    $[19] = classes.icon;
    $[20] = classes.row;
    $[21] = showAutomations;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== classes.icon) {
    t10 = <RowContents icon={Plug} label="MCP Connections" iconClassName={classes.icon} />;
    $[23] = classes.icon;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== classes.row || $[26] !== t10) {
    t11 = <div className={classes.row}>{t10}</div>;
    $[25] = classes.row;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== classes.icon) {
    t12 = <RowContents icon={Layers} label="Machine Definitions" iconClassName={classes.icon} />;
    $[28] = classes.icon;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== classes.row || $[31] !== t12) {
    t13 = <div className={classes.row}>{t12}</div>;
    $[30] = classes.row;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t13 || $[35] !== t3 || $[36] !== t6 || $[37] !== t8 || $[38] !== t9) {
    t14 = <nav className={t3}>{t6}{t8}{t9}{t11}{t13}</nav>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t3;
    $[36] = t6;
    $[37] = t8;
    $[38] = t9;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
}